/**
 * PageROSP — "Suivi ROSP" tab (Rémunération sur objectifs de santé publique).
 * Lists the médecin-traitant patients with 3 top indicators (vaccination,
 * dépistage, suivi chronique) + counters at the top. Sandbox-only.
 */
import React from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Card, PageHeader } from "./shared";
import { SEED_PATIENTS, ROSP_INDICATORS } from "./data";

export function PageROSP({ setView }) {
  const mtPatients = SEED_PATIENTS.filter((p) => p.isMT);
  const trackedCount = 11; // sandbox: 11/15 indicateurs suivis pour cet exercice

  const StatusPill = ({ ok, warn, label }) => (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium ${
      ok ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
        : warn ? "bg-amber-100 text-amber-800 border border-amber-200"
        : "bg-red-100 text-red-700 border border-red-200"
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );

  const rowState = (p) => {
    const r = p.rosp || {};
    const missing =
      (r.vaccinationUpToDate ? 0 : 1) +
      (r.screeningInProgress ? 0 : 1) +
      (r.chronicFollowUp === "up-to-date" || r.chronicFollowUp === "n/a" ? 0 : 1);
    return missing > 0;
  };

  return (
    <div data-testid="page-rosp">
      <PageHeader
        title="Suivi ROSP"
        subtitle="Rémunération sur objectifs de santé publique — indicateurs médecin traitant en temps réel"
      />

      {/* Compteur global */}
      <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Indicateurs suivis</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-[28px] font-bold text-slate-900 leading-none">{trackedCount}<span className="text-[16px] text-slate-400">/{ROSP_INDICATORS.length}</span></div>
            <span className="text-[11px] text-slate-500">indicateurs actifs</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(trackedCount / ROSP_INDICATORS.length) * 100}%`, backgroundColor: "#4FC3F7" }} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Patients médecin traitant</div>
          <div className="mt-1 text-[28px] font-bold text-slate-900 leading-none" data-testid="rosp-mt-count">{mtPatients.length}</div>
          <div className="mt-1 text-[11px] text-slate-500">Suivi ROSP · Actifs sur les 12 derniers mois</div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Actions requises</div>
          <div className="mt-1 text-[28px] font-bold text-red-600 leading-none" data-testid="rosp-action-count">{mtPatients.filter(rowState).length}</div>
          <div className="mt-1 text-[11px] text-slate-500">Patients avec au moins un indicateur en retard</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 grid grid-cols-[1.4fr,1fr,1.2fr,1.2fr,1fr] gap-3 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
          <div>Patient</div>
          <div>Vaccination</div>
          <div>Dépistage</div>
          <div>Suivi chronique</div>
          <div className="text-right">Action</div>
        </div>
        <div className="divide-y divide-slate-100">
          {mtPatients.map((p) => {
            const r = p.rosp || {};
            const chronic = r.chronicFollowUp;
            const needAction = rowState(p);
            return (
              <div key={p.id} className="grid grid-cols-[1.4fr,1fr,1.2fr,1.2fr,1fr] gap-3 px-4 py-3 items-center text-[13px] hover:bg-slate-50" data-testid={`rosp-row-${p.id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{p.first} {p.last}</div>
                  <div className="text-[11.5px] text-slate-500">{p.age} ans · {p.ald?.length ? p.ald.join(", ") : "sans ALD"}</div>
                </div>
                <div><StatusPill ok={r.vaccinationUpToDate} label={r.vaccinationUpToDate ? "À jour" : "Non"} /></div>
                <div><StatusPill ok={r.screeningInProgress} warn={!r.screeningInProgress} label={r.screeningInProgress ? "En cours" : "À initier"} /></div>
                <div>
                  {chronic === "n/a"
                    ? <span className="text-[11.5px] text-slate-400">N/A</span>
                    : <StatusPill ok={chronic === "up-to-date"} label={chronic === "up-to-date" ? "À jour" : "En retard"} />
                  }
                </div>
                <div className="text-right">
                  {needAction ? (
                    <button
                      onClick={() => { toast.info(`Ouverture de la fiche patient ${p.first} ${p.last} (sandbox).`); setView && setView("dossiers"); }}
                      data-testid={`rosp-action-${p.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
                    >
                      <AlertTriangle className="w-3 h-3" /> Action requise
                    </button>
                  ) : (
                    <span className="text-[11.5px] text-emerald-600 font-medium">Complet</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="mt-4 text-[11.5px] text-slate-500 flex flex-wrap items-center gap-4">
        <span className="flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Indicateurs mis à jour en temps réel à partir des consultations et documents patients.</span>
        <span>Barème CNAM 2026 · {ROSP_INDICATORS.length} indicateurs officiels</span>
      </div>
    </div>
  );
}
